/**
 * What a pop-out window shows once the session has ended (TRUS-638).
 *
 * AuthProvider renders this IN PLACE OF the whole app in a top-level pop-out
 * (tabbed host, page pop-out opened on its own, desktop app-* / messaging-*
 * window) as soon as it signs out — so nothing the window had loaded stays on
 * screen or in the DOM. It first tries to close the window: a desktop window
 * can close itself; a browser only lets script close a window that script
 * opened. If the window is still here a moment later (a tab the user opened, or
 * one opened with noopener), it says so and offers a way back to sign-in.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** How long to wait for window.close() to take effect before showing the message. */
export const CLOSE_FALLBACK_MS = 300;

export async function closeThisWindow(win: Window = window): Promise<void> {
  if (typeof win !== 'undefined' && '__TAURI__' in win) {
    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      await getCurrentWindow().close();
      return;
    } catch {
      /* fall back to window.close below */
    }
  }
  try {
    win.close();
  } catch {
    /* not closable: the message shows */
  }
}

export function PopoutSignedOutScreen({ fallbackDelayMs = CLOSE_FALLBACK_MS }: { fallbackDelayMs?: number }) {
  const { t } = useTranslation();
  const [stillOpen, setStillOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void closeThisWindow();
    const timer = setTimeout(() => {
      if (!cancelled) setStillOpen(true);
    }, fallbackDelayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fallbackDelayMs]);

  useEffect(() => {
    try {
      document.title = 'WeaveIn';
    } catch {
      /* ignore */
    }
  }, []);

  if (!stillOpen) {
    // Closing: show nothing at all, so the app never flashes back.
    return <div data-testid="signed-out-closing" className="min-h-screen bg-background" />;
  }

  return (
    <div
      data-testid="signed-out-screen"
      role="main"
      className="min-h-screen flex items-center justify-center bg-background px-4"
    >
      <div className="max-w-sm w-full text-center space-y-4">
        <h1 className="text-xl font-semibold text-foreground">
          {t('appPopout.signed_out_title', "You've been signed out")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('appPopout.signed_out_body', 'Your WeaveIn session has ended. You can close this tab.')}
        </p>
        <button
          type="button"
          data-testid="signed-out-sign-in"
          onClick={() => window.location.assign('/')}
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          {t('appPopout.sign_in_again', 'Sign in again')}
        </button>
      </div>
    </div>
  );
}

export default PopoutSignedOutScreen;
